/**
 * Captured Pieces Component
 * Shows pieces that have been captured by each player
 */

import React, { useMemo } from 'react';
import type { GameState, Color, Piece, ChessMove, JesterMove } from '@jester-chess/shared';
import './CapturedPieces.css';

interface CapturedPiecesProps {
  gameState: GameState;
  yourColor: Color;
}

// Helper to check if a move is a ChessMove
function isChessMove(move: ChessMove | JesterMove): move is ChessMove {
  return 'from' in move && 'to' in move;
}

// Helper to check if a move is a JesterMove
function isJesterMove(move: ChessMove | JesterMove): move is JesterMove {
  return 'diceRoll' in move && 'targetSquare' in move;
}

// Get piece symbol for display
function getPieceSymbol(piece: Piece): string {
  switch (piece.type) {
    case 'king':
      return piece.color === 'white' ? '♔' : '♚';
    case 'queen':
      return piece.color === 'white' ? '♕' : '♛';
    case 'rook':
      return piece.color === 'white' ? '♖' : '♜';
    case 'bishop':
      return piece.color === 'white' ? '♗' : '♝';
    case 'knight':
      return piece.color === 'white' ? '♘' : '♞';
    case 'pawn':
      return piece.color === 'white' ? '♙' : '♟';
    case 'jester':
      return piece.color === 'white' ? '⚪' : '⚫';
    default:
      return '';
  }
}

// Piece value for sorting (higher value = more important)
function getPieceValue(type: string): number {
  switch (type) {
    case 'queen': return 9;
    case 'rook': return 5;
    case 'bishop': return 3;
    case 'knight': return 3;
    case 'pawn': return 1;
    case 'jester': return 0;
    default: return 0;
  }
}

export const CapturedPieces: React.FC<CapturedPiecesProps> = ({ gameState, yourColor }) => {
  const opponentColor = yourColor === 'white' ? 'black' : 'white';

  // Extract captured pieces from moves
  const { yourCaptures, opponentCaptures } = useMemo(() => {
    const yourCaptures: Piece[] = [];
    const opponentCaptures: Piece[] = [];

    for (const move of gameState.moves) {
      if (isChessMove(move) && move.capturedPiece) {
        // The color of the captured piece tells us who captured it
        // If captured piece is opponent's color, you captured it
        if (move.capturedPiece.color === opponentColor) {
          yourCaptures.push(move.capturedPiece);
        } else {
          opponentCaptures.push(move.capturedPiece);
        }
      } else if (isJesterMove(move) && move.capturedPiece) {
        // Jester captures - jester belongs to the player who moved it
        // So if captured piece is opponent's color, current player captured it
        if (move.capturedPiece.color === opponentColor) {
          yourCaptures.push(move.capturedPiece);
        } else {
          opponentCaptures.push(move.capturedPiece);
        }
      }
    }

    // Sort by piece value (highest first)
    yourCaptures.sort((a, b) => getPieceValue(b.type) - getPieceValue(a.type));
    opponentCaptures.sort((a, b) => getPieceValue(b.type) - getPieceValue(a.type));

    return { yourCaptures, opponentCaptures };
  }, [gameState.moves, opponentColor]);

  return (
    <div className="captured-pieces-container">
      {/* Opponent's captures (your pieces they took) - shown at top */}
      <div className="captured-section opponent-captures">
        <div className="captured-label">
          {opponentColor === 'white' ? 'White' : 'Black'} captured:
        </div>
        <div className="captured-pieces-row">
          {opponentCaptures.length === 0 ? (
            <span className="no-captures">—</span>
          ) : (
            opponentCaptures.map((piece, index) => (
              <span 
                key={`opp-${index}`} 
                className={`captured-piece ${piece.color}`}
                title={piece.type}
              >
                {getPieceSymbol(piece)}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Your captures (opponent's pieces you took) - shown at bottom */}
      <div className="captured-section your-captures">
        <div className="captured-label">
          {yourColor === 'white' ? 'White' : 'Black'} captured:
        </div>
        <div className="captured-pieces-row">
          {yourCaptures.length === 0 ? (
            <span className="no-captures">—</span>
          ) : (
            yourCaptures.map((piece, index) => (
              <span 
                key={`your-${index}`} 
                className={`captured-piece ${piece.color}`}
                title={piece.type}
              >
                {getPieceSymbol(piece)}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

